export interface PromptImportColumn {
  index: number;
  label: string;
  preview: string;
}

export interface PromptImportResult {
  prompts: string[];
  topics?: string[];
  /** Present only when the user must choose a column. */
  table?: { columns: PromptImportColumn[]; rows: string[][]; hasHeader: boolean };
}

export function promptsFromText(text: string): string[] {
  return text
    .replace(/^\uFEFF/, '')
    .split(/\r\n|\n|\r/)
    .map(line => line.trim())
    .filter(Boolean);
}

/** RFC 4180 fields, including quoted newlines. Never truncates rows or field text. */
export function parsePromptCsv(text: string): string[][] {
  const input = text.replace(/^\uFEFF/, '');
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let closedQuote = false;
  const finishField = () => {
    row.push(field);
    field = '';
    closedQuote = false;
  };
  const finishRow = () => {
    finishField();
    if (row.some(value => value.trim())) rows.push(row);
    row = [];
  };
  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    if (quoted) {
      if (char === '"') {
        if (input[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          quoted = false;
          closedQuote = true;
        }
      } else {
        field += char;
      }
    } else if (char === ',') {
      finishField();
    } else if (char === '\n' || char === '\r') {
      finishRow();
      if (char === '\r' && input[index + 1] === '\n') index += 1;
    } else if (char === '"' && !field && !closedQuote) {
      quoted = true;
    } else if (closedQuote) {
      if (char !== ' ' && char !== '\t') {
        throw new Error('This CSV has text after a closing quote. Check the file and try again.');
      }
    } else if (char === '"') {
      throw new Error(
        'This CSV has an unexpected quote. Put fields containing quotes in double quotes.'
      );
    } else {
      field += char;
    }
  }
  if (quoted) throw new Error('This CSV has an unclosed quote. Check the file and try again.');
  if (field || row.length || closedQuote) finishRow();
  return rows;
}

const PROMPT_HEADERS = new Set(['prompt', 'prompts', 'question', 'questions', 'query', 'queries']);
const METADATA_HEADERS = new Set([
  'id',
  'stage',
  'category',
  'topic',
  'brand',
  'intent',
  'group',
  'country',
  'location',
  'label',
]);

export function selectPromptColumn(rows: string[][], column: number, hasHeader: boolean): string[] {
  if (!Number.isInteger(column) || column < 0 || !rows.some(row => column < row.length)) {
    throw new Error('Choose the column containing your prompts.');
  }
  return rows
    .slice(hasHeader ? 1 : 0)
    .map(row => (row[column] || '').trim())
    .filter(Boolean);
}

export function importPrompts(text: string, format: 'csv' | 'txt'): PromptImportResult {
  if (format === 'txt') return { prompts: promptsFromText(text) };
  const rows = parsePromptCsv(text);
  if (!rows.length) return { prompts: [] };
  const width = rows.reduce((maximum, row) => Math.max(maximum, row.length), 0);
  const headers = rows[0].map(value => value.trim().toLowerCase());
  const promptColumns = headers.flatMap((value, index) =>
    PROMPT_HEADERS.has(value) ? [index] : []
  );
  const hasHeader = promptColumns.length > 0 || headers.every(value => METADATA_HEADERS.has(value));
  const topicColumns = headers.flatMap((value,index)=>['topic','group','category'].includes(value)?[index]:[]);
  if (promptColumns.length === 1 && topicColumns.length <= 1) {
    return selectPromptTopicColumns(rows,promptColumns[0],true,topicColumns[0]);
  }
  if (width === 1) return { prompts: selectPromptColumn(rows, 0, hasHeader) };
  return {
    prompts: [],
    table: {
      rows,
      hasHeader,
      columns: Array.from({ length: width }, (_, index) => ({
        index,
        label: hasHeader ? rows[0][index] || `Column ${index + 1}` : `Column ${index + 1}`,
        preview: rows[hasHeader ? 1 : 0]?.[index] || '',
      })),
    },
  };
}

export function selectPromptTopicColumns(rows:string[][],column:number,hasHeader:boolean,topicColumn?:number):PromptImportResult {
  selectPromptColumn(rows,column,hasHeader); // shared column validation
  if(topicColumn!==undefined && (!Number.isInteger(topicColumn)||topicColumn<0||topicColumn===column||!rows.some(row=>topicColumn<row.length))) throw new Error('Choose a separate topic column.');
  const selected=rows.slice(hasHeader?1:0).filter(row=>(row[column]||'').trim());
  return {prompts:selected.map(row=>row[column].trim()),...(topicColumn!==undefined?{topics:selected.map(row=>(row[topicColumn]||'').trim())}:{})};
}
